// Client-side mirror of fn_quote_compute_totals (supabase/migrations/20260905000200_quotes.sql)
// so the builder and the public acceptance page can show totals LIVE while the user edits or
// chooses options. Integer arithmetic only (BigInt on minor units; decimals scaled to 4 places
// exactly as numeric(18,4) stores them); rounding is half away from zero like Postgres round().
// The server's figure always wins: every write re-reads quote.totals from the API.

export const INTERVALS = ['monthly', 'quarterly', 'semi_annual', 'annual'] as const;
export type Interval = (typeof INTERVALS)[number];
export type BucketKey = 'one_time' | Interval;

export interface TotalsLine {
  id: string;
  section_id: string | null;
  qty: string | number;
  unit_price_minor: string | number;
  unit_cost_minor?: string | number | null;
  discount_percent: string | number;
  tax_rate_percent?: string | number | null;
  kind: 'one_off' | 'recurring';
  recurring_interval: string | null;
  is_selected: boolean;
}

export interface TotalsQuote {
  discount_type: 'none' | 'percent' | 'amount';
  discount_value: string | number;
  tax_mode: 'exclusive' | 'inclusive' | 'none';
}

export interface Bucket {
  subtotal_minor: string;
  discount_minor: string;
  net_minor: string;
  tax_minor: string;
  total_minor: string;
  cost_minor: string;
  margin_minor: string;
}

export interface ComputedTotals {
  one_time: Bucket;
  recurring: Record<Interval, Bucket>;
  line_count: number;
  selected_line_count: number;
}

const SCALE = 10_000n; // numeric(18,4)

/** "12.5" → 125000n (4 decimal places), exact; non-numeric → 0n. */
export function scaled(v: string | number | null | undefined): bigint {
  const s = String(v ?? '').trim();
  const m = /^(-)?(\d*)(?:\.(\d*))?$/.exec(s);
  if (!m || (m[2] === '' && !m[3])) return 0n;
  const whole = BigInt(m[2] || '0');
  const frac = BigInt((m[3] ?? '').padEnd(4, '0').slice(0, 4));
  const out = whole * SCALE + frac;
  return m[1] ? -out : out;
}

/** Minor units (bigint-as-string from the API, or a number) → bigint; garbage → 0n. */
export function minor(v: string | number | bigint | null | undefined): bigint {
  if (typeof v === 'bigint') return v;
  const s = String(v ?? '').trim();
  return /^-?\d+$/.test(s) ? BigInt(s) : 0n;
}

/** Integer division rounded half away from zero (Postgres round()). */
export function roundDiv(n: bigint, d: bigint): bigint {
  if (d < 0n) {
    n = -n;
    d = -d;
  }
  const neg = n < 0n;
  const a = neg ? -n : n;
  const q = (a + d / 2n) / d;
  return neg ? -q : q;
}

/** qty × unit × (1 − discount%) in minor units — the generated column line_total_minor. */
export function lineTotal(l: Pick<TotalsLine, 'qty' | 'unit_price_minor' | 'discount_percent'>): bigint {
  const q = scaled(l.qty); // ×1e4
  const d = scaled(l.discount_percent); // ×1e4, percent
  // qty×1e4 × unit × (100×1e4 − d) / (1e4 × 100 × 1e4)
  return roundDiv(q * minor(l.unit_price_minor) * (100n * SCALE - d), SCALE * 100n * SCALE);
}

function lineTax(total: bigint, rate: string | number | null | undefined, mode: TotalsQuote['tax_mode']): bigint {
  const r = scaled(rate);
  if (mode === 'none' || r === 0n) return 0n;
  // exclusive: total × r/100 ; inclusive: total − total×100/(100+r) = total × r / (100 + r)
  return mode === 'exclusive' ? roundDiv(total * r, 100n * SCALE) : roundDiv(total * r, 100n * SCALE + r);
}

function bucketOf(lines: TotalsLine[], q: TotalsQuote, key: BucketKey): Bucket {
  let sub = 0n;
  let cost = 0n;
  let tax = 0n;
  for (const l of lines) {
    if (!l.is_selected) continue;
    const inBucket = key === 'one_time' ? l.kind === 'one_off' : l.kind === 'recurring' && l.recurring_interval === key;
    if (!inBucket) continue;
    const t = lineTotal(l);
    sub += t;
    cost += roundDiv(scaled(l.qty) * minor(l.unit_cost_minor ?? 0), SCALE);
    tax += lineTax(t, l.tax_rate_percent, q.tax_mode);
  }
  let disc = 0n;
  if (q.discount_type === 'percent') disc = roundDiv(sub * scaled(q.discount_value), 100n * SCALE);
  else if (q.discount_type === 'amount' && key === 'one_time') {
    const amt = scaled(q.discount_value) / SCALE;
    disc = amt < sub ? amt : sub;
  }
  if (sub > 0n && disc > 0n) tax = roundDiv(tax * (sub - disc), sub);
  const net = sub - disc;
  const total = q.tax_mode === 'exclusive' ? net + tax : net;
  const margin = (q.tax_mode === 'inclusive' ? net - tax : net) - cost;
  const s = (b: bigint) => b.toString();
  return { subtotal_minor: s(sub), discount_minor: s(disc), net_minor: s(net), tax_minor: s(tax), total_minor: s(total), cost_minor: s(cost), margin_minor: s(margin) };
}

export function computeTotals(lines: TotalsLine[], q: TotalsQuote): ComputedTotals {
  const recurring = {} as Record<Interval, Bucket>;
  for (const i of INTERVALS) recurring[i] = bucketOf(lines, q, i);
  return { one_time: bucketOf(lines, q, 'one_time'), recurring, line_count: lines.length, selected_line_count: lines.filter((l) => l.is_selected).length };
}

export const INTERVAL_LABEL: Record<Interval, string> = { monthly: 'per month', quarterly: 'per quarter', semi_annual: 'per 6 months', annual: 'per year' };
export const INTERVAL_SHORT: Record<Interval, string> = { monthly: '/mo', quarterly: '/qtr', semi_annual: '/6mo', annual: '/yr' };

// ── selection rules (mirror of src/modules/quoting/domain.ts) ────────────────
export interface SelSection {
  id: string;
  selection: 'all' | 'single' | 'multi';
  is_optional: boolean;
}
export interface SelLine {
  id: string;
  section_id: string | null;
  is_optional: boolean;
}

export function isChoosable(line: SelLine, section: SelSection | undefined): boolean {
  if (section && section.selection !== 'all') return true;
  return line.is_optional;
}

/** Human-readable rule violations for the current choices (empty = valid). */
export function selectionProblems(sections: (SelSection & { title: string })[], lines: SelLine[], chosen: Set<string>): string[] {
  const out: string[] = [];
  for (const s of sections) {
    const inSection = lines.filter((l) => l.section_id === s.id);
    if (!inSection.length || s.selection === 'all') continue;
    const n = inSection.filter((l) => chosen.has(l.id)).length;
    if (s.selection === 'single' && n > 1) out.push(`Choose only one option in “${s.title}”.`);
    if (s.selection === 'single' && n === 0 && !s.is_optional) out.push(`Choose one option in “${s.title}”.`);
    if (s.selection === 'multi' && n === 0 && !s.is_optional) out.push(`Choose at least one option in “${s.title}”.`);
  }
  return out;
}
